import { useCallback, useState } from "react";
import { useApi, type Token } from "../lib/api.ts";
import { useAsync } from "../lib/useAsync.ts";
import { formatDate, formatRelative } from "../lib/format.ts";
import { TokenCreateModal } from "../components/TokenCreateModal.tsx";
import {
  EmptyState,
  ErrorState,
  PageHeader,
  Spinner,
} from "../components/States.tsx";

export default function Tokens() {
  const api = useApi();
  const fetcher = useCallback(
    (signal: AbortSignal) => api.listTokens(signal),
    [api],
  );
  const { data, error, loading, reload } = useAsync(fetcher, [api]);
  const [creating, setCreating] = useState(false);

  return (
    <section>
      <PageHeader
        title="API tokens"
        subtitle="Tokens authenticate the CLI against sync.stift.sh for this org."
        actions={
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setCreating(true)}
          >
            Create token
          </button>
        }
      />

      {loading && <Spinner />}
      {error && <ErrorState error={error} onRetry={reload} />}

      {data && data.length === 0 && (
        <EmptyState title="No tokens yet">
          <p>
            Create a token to connect the CLI. You&apos;ll see the secret once,
            right after creating it.
          </p>
        </EmptyState>
      )}

      {data && data.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Created</th>
                <th>Last used</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {data.map((token) => (
                <TokenRow key={token.id} token={token} onChange={reload} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <TokenCreateModal
          onClose={() => setCreating(false)}
          onCreated={reload}
        />
      )}
    </section>
  );
}

function TokenRow({
  token,
  onChange,
}: {
  token: Token;
  onChange: () => void;
}) {
  const api = useApi();
  const [revoking, setRevoking] = useState(false);

  async function revoke() {
    if (revoking) return;
    if (
      !window.confirm(
        `Revoke “${token.name}”? Any CLI using it will stop working immediately.`,
      )
    ) {
      return;
    }
    setRevoking(true);
    try {
      await api.revokeToken(token.id);
      onChange();
    } catch {
      setRevoking(false);
      // TODO: surface a toast on failure.
    }
  }

  return (
    <tr>
      <td className="mono">{token.name}</td>
      <td>
        {token.admin ? (
          <span className="badge badge--admin">admin</span>
        ) : (
          <span className="badge">member</span>
        )}
      </td>
      <td className="dim" title={token.created_at}>
        {formatDate(token.created_at)}
      </td>
      <td className="dim" title={token.last_used ?? "never"}>
        {formatRelative(token.last_used)}
      </td>
      <td className="num">
        <button
          type="button"
          className="btn btn--danger btn--sm"
          onClick={revoke}
          disabled={revoking}
        >
          {revoking ? "Revoking…" : "Revoke"}
        </button>
      </td>
    </tr>
  );
}
